import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from '../lib/supabase'
import { setSentryUser, clearSentryUser } from '../lib/sentry'
import { setPosthogUser, clearPosthogUser } from '../lib/posthog'
import { queryClient } from '../lib/queries/queryClient'
import { leadsQueryKey, fetchLeads } from '../lib/queries/useLeads'
import { isStudioTier } from '../lib/studioTier'

const AuthContext = createContext(null)

const OWNER_PERMISSIONS = {
  banking: true,
  pl: true,
  lead_analytics: true,
  client_financials: true,
  settings: true,
}

const DEFAULT_MEMBER_PERMISSIONS = {
  banking: false,
  pl: false,
  lead_analytics: false,
  client_financials: true,
  settings: false,
}

// ─── Paywall thresholds ─────────────────────────────────────────────────────
// Per product memory (project_paywall.md): retrospective-value at day 10 + 3rd
// event, not pure time-based trial. The hard-block still waits for day 15 —
// the earlier trigger is a soft nudge that shows the UpgradeV2 retrospective-
// stats page when the planner has actually *used* the product enough for the
// upgrade decision to feel earned.
const TRIAL_DAYS       = 14    // hard-block at day > TRIAL_DAYS
const NUDGE_AFTER_DAYS = 10    // soft nudge once day >= this
const NUDGE_AFTER_EVENTS = 3   // soft nudge once event count >= this

// Paid subscription tiers — anything in this set gets full access; 'trial' / null
// does not. IMPORTANT: the Stripe webhook (stripe-webhook.js) maps the Starter
// price → 'starter', so 'starter' MUST be listed here or a paying Starter user
// falls through to the trial branch and gets hard-blocked on day 15.
// 'team_member' is intentionally NOT here — a Studio associate's access cascades
// from the OWNING Studio's subscription, handled in its own branch in checkAccess
// (granted while the owner is active; revoked the moment the owner's plan lapses).
const PAID_TIERS = ['starter', 'solo', 'studio']

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [profile, setProfile] = useState(null)
  const [permissions, setPermissions] = useState(OWNER_PERMISSIONS)
  const [loading, setLoading] = useState(true)
  const [accountSettings, setAccountSettings] = useState(null)
  const [accessBlocked, setAccessBlocked] = useState(false)
  // Paywall state for the retrospective-value nudge (day 10+ OR 3+ events).
  //   upgradePromptDue:   true when we should surface the soft upsell banner/
  //                       modal. False once paid or once hard-blocked.
  //   trialDaysLeft:      nullable int — useful for banner copy (“3 days left”).
  //                       null for paid users.
  //   eventCount:         live count used for the 3rd-event trigger. Cached
  //                       at checkAccess time; UI can pass it along to the
  //                       UpgradeV2 retrospective stats without a re-query.
  //   subscribed:         boolean shortcut (any paid tier — see PAID_TIERS).
  const [paywallState, setPaywallState] = useState({
    upgradePromptDue: false,
    trialDaysLeft: null,
    eventCount: 0,
    subscribed: false,
  })

  // Refs for use inside callbacks without stale closures
  const accountSettingsRef = useRef(null)
  const ownerProfileIdRef = useRef(null)
  const profileRef = useRef(null)
  const permissionsRef = useRef(OWNER_PERMISSIONS)

  const loadProfile = useCallback(async (userId) => {
    if (!userId) {
      setProfile(null)
      setPermissions(OWNER_PERMISSIONS)
      setAccountSettings(null)
      accountSettingsRef.current = null
      ownerProfileIdRef.current = null
      profileRef.current = null
      permissionsRef.current = OWNER_PERMISSIONS
      return
    }

    const { data } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single()

    setProfile(data || null)
    profileRef.current = data || null

    // Frictionless invite acceptance — when a member's profile loads
    // (whether on first magic-link sign-in or any subsequent session),
    // stamp accepted_at on any pending team_invitations row matching
    // this user's email + owner_id. Idempotent: the RPC is a no-op
    // when nothing's pending. Without this, the invite flow's
    // post-magic-link redirect skips JoinTeam.jsx and the planner's
    // Pending Invitations list shows the invitee as "pending" forever
    // even after they're using the app daily. See migration
    // 20260603c_auto_accept_team_invitation.sql.
    //
    // Fire-and-forget — never block profile load. Errors are
    // non-fatal; worst case the Pending list stays stale and a
    // human can revoke from Settings.
    if (data?.role === 'member' && data?.owner_id) {
      supabase
        .rpc('auto_accept_team_invitation_for_user')
        .then(({ error }) => {
          if (error) console.warn('[AuthContext] auto-accept invitation failed:', error)
        })
    }

    if (!data) {
      setPermissions(OWNER_PERMISSIONS)
      permissionsRef.current = OWNER_PERMISSIONS
      ownerProfileIdRef.current = userId
      setAccountSettings(null)
      accountSettingsRef.current = null
    } else if (data.role === 'member') {
      const perms = data.permissions || DEFAULT_MEMBER_PERMISSIONS
      setPermissions(perms)
      permissionsRef.current = perms

      // Members read settings from their owner's profile
      if (data.owner_id) {
        ownerProfileIdRef.current = data.owner_id
        const { data: ownerProfile } = await supabase
          .from('profiles')
          .select('settings')
          .eq('id', data.owner_id)
          .single()
        const settings = ownerProfile?.settings || null
        setAccountSettings(settings)
        accountSettingsRef.current = settings
      } else {
        ownerProfileIdRef.current = userId
        const settings = data.settings || null
        setAccountSettings(settings)
        accountSettingsRef.current = settings
      }
    } else {
      // Owner — full access, own settings
      setPermissions(OWNER_PERMISSIONS)
      permissionsRef.current = OWNER_PERMISSIONS
      ownerProfileIdRef.current = userId
      const settings = data.settings || null
      setAccountSettings(settings)
      accountSettingsRef.current = settings
    }
  }, [])

  const checkAccess = useCallback(async (userId) => {
    if (!userId) {
      setAccessBlocked(false)
      setPaywallState({ upgradePromptDue: false, trialDaysLeft: null, eventCount: 0, subscribed: false })
      return
    }

    // Pull profile + event count in parallel — the 3rd-event retrospective
    // trigger needs the count, and paying one round-trip for it is cheap.
    const [accessRes, eventCountRes] = await Promise.all([
      supabase
        .from('profiles')
        .select('trial_start, subscription_tier, subscription_expires_at, owner_id')
        .eq('id', userId)
        .single(),
      supabase
        .from('events')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', userId),
    ])
    const accessProfile = accessRes.data
    const eventCount = eventCountRes.count || 0

    if (!accessProfile) return

    // Team associate (team_member): access CASCADES from the owning Studio. The
    // associate is active whenever the owner is — paid-and-not-expired, or still
    // within their trial window — and is blocked the moment the owner's plan
    // lapses/cancels. Fail-open if the owner can't be resolved, so a data hiccup
    // never locks out a legitimate associate.
    if (accessProfile.subscription_tier === 'team_member') {
      let ownerActive = true // fail-open default (missing owner_id / owner row)
      if (accessProfile.owner_id) {
        const { data: ownerProfile } = await supabase
          .from('profiles')
          .select('subscription_tier, subscription_expires_at, trial_start')
          .eq('id', accessProfile.owner_id)
          .maybeSingle()
        if (ownerProfile) {
          if (PAID_TIERS.includes(ownerProfile.subscription_tier)) {
            ownerActive = ownerProfile.subscription_expires_at
              ? new Date(ownerProfile.subscription_expires_at) >= new Date()
              : true
          } else {
            // Owner on trial / no paid plan — mirror their trial clock.
            const oStart = ownerProfile.trial_start ? new Date(ownerProfile.trial_start) : null
            const oElapsed = oStart ? Math.floor((new Date() - oStart) / (1000 * 60 * 60 * 24)) : 0
            ownerActive = oElapsed <= TRIAL_DAYS
          }
        }
      }
      setAccessBlocked(!ownerActive)
      setPaywallState({ upgradePromptDue: false, trialDaysLeft: null, eventCount, subscribed: ownerActive })
      return
    }

    // Active paid subscription — no paywall at all. `accessBlocked` still
    // fires when a paid subscription lapses (expired timestamp).
    if (PAID_TIERS.includes(accessProfile.subscription_tier)) {
      if (accessProfile.subscription_expires_at) {
        const expired = new Date(accessProfile.subscription_expires_at) < new Date()
        if (expired) {
          setAccessBlocked(true)
          setPaywallState({ upgradePromptDue: false, trialDaysLeft: null, eventCount, subscribed: false })
          return
        }
      }
      setAccessBlocked(false)
      setPaywallState({ upgradePromptDue: false, trialDaysLeft: null, eventCount, subscribed: true })
      return
    }

    // Trial — compute elapsed + remaining.
    const trialStart = accessProfile.trial_start ? new Date(accessProfile.trial_start) : new Date()
    const elapsed = Math.floor((new Date() - trialStart) / (1000 * 60 * 60 * 24))
    const trialDaysLeft = Math.max(0, TRIAL_DAYS - elapsed)

    // Hard block once trial has fully expired (day > 14).
    if (elapsed > TRIAL_DAYS) {
      setAccessBlocked(true)
      setPaywallState({ upgradePromptDue: false, trialDaysLeft: 0, eventCount, subscribed: false })
      return
    }

    // Soft nudge — day 10+ OR 3+ events. This is the retrospective-value
    // moment: the planner has built enough artifacts that UpgradeV2's stats
    // page tells a real story.
    const upgradePromptDue =
      elapsed >= NUDGE_AFTER_DAYS || eventCount >= NUDGE_AFTER_EVENTS

    setAccessBlocked(false)
    setPaywallState({
      upgradePromptDue,
      trialDaysLeft,
      eventCount,
      subscribed: false,
    })
  }, [])

  useEffect(() => {
    // Initial session load — await checkAccess so accessBlocked is set before loading=false
    supabase.auth.getSession()
      .then(async ({ data: { session } }) => {
        const u = session?.user ?? null
        setUser(u)
        // Tag Sentry issues with the current user so we know which planner
        // hit what. No-op when Sentry isn't configured.
        if (u) setSentryUser(u)
        else clearSentryUser()
        // Same shape for PostHog person identification — aliases any
        // pre-signup anonymous distinct_id onto the Supabase user id so
        // earlier funnel events still attribute correctly.
        if (u) setPosthogUser(u)
        else clearPosthogUser()
        try {
          if (u?.id) {
            // Prefetch leads in parallel with auth setup so the home page
            // renders immediately after loading=false instead of waiting
            // for a second round trip.
            queryClient.prefetchQuery({ queryKey: leadsQueryKey(u.id), queryFn: () => fetchLeads(u.id) })
            // loadProfile and checkAccess are independent — run in parallel
            await Promise.all([loadProfile(u.id), checkAccess(u.id)])
            // No demo auto-seed. New planners start on a clean, empty dashboard
            // (onboarding Step 1) instead of a pre-populated Chen sample that
            // auto-advanced their onboarding and surfaced seeded contracts as
            // "awaiting your signature." The sample is now opt-in only — the
            // "See a sample event" lane (OnboardingHero / Welcome / intake demo
            // path) calls seedDemoEventIfNeeded on demand.
          } else {
            await loadProfile(null)
          }
        } catch (err) {
          console.error('[AuthContext] init error:', err)
        } finally {
          setLoading(false)
        }
      })
      .catch((err) => {
        // getSession() failed (network error, etc.) — unblock the app
        console.error('[AuthContext] getSession failed:', err)
        setLoading(false)
      })

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      const u = session?.user ?? null
      setUser(u)
      if (u) setSentryUser(u)
      else clearSentryUser()
      if (u) setPosthogUser(u)
      else clearPosthogUser()
      if (u?.id) {
        queryClient.prefetchQuery({ queryKey: leadsQueryKey(u.id), queryFn: () => fetchLeads(u.id) })
        Promise.all([loadProfile(u.id), checkAccess(u.id)])
          // No demo auto-seed here either — see the getSession branch above.
          .catch((err) => console.error('[AuthContext] onAuthStateChange error:', err))
      } else {
        loadProfile(null).catch((err) => console.error('[AuthContext] loadProfile error:', err))
        setAccessBlocked(false)
      }
    })

    return () => subscription.unsubscribe()
  }, [loadProfile, checkAccess])

  const refreshProfile = () => loadProfile(user?.id)

  const hasPermission = (key) => {
    if (!profileRef.current || profileRef.current.role !== 'member') return true
    return !!(permissionsRef.current?.[key])
  }

  const updateAccountSettings = useCallback(async (key, value) => {
    const ownerId = ownerProfileIdRef.current
    if (!ownerId) return

    // Team members without settings permission cannot write
    if (profileRef.current?.role === 'member' && !permissionsRef.current?.settings) return

    const current = accountSettingsRef.current || {}
    const updated = { ...current, [key]: value }
    accountSettingsRef.current = updated
    setAccountSettings(updated)

    try {
      await supabase
        .from('profiles')
        .update({ settings: updated })
        .eq('id', ownerId)
    } catch (err) {
      console.error('Failed to save settings to Supabase:', err)
    }
  }, [])

  return (
    <AuthContext.Provider value={{
      user, profile, permissions, loading, refreshProfile, hasPermission,
      accountSettings, updateAccountSettings, accessBlocked,
      // Studio gate — true for a studio owner or a studio associate. The single
      // source of truth (isStudioTier) for the Team Chat surfaces, which are
      // hidden for solo/starter accounts. Computed off the loaded profile's
      // subscription_tier (profiles is selected with '*', so the column is here).
      isStudio: isStudioTier(profile?.subscription_tier),
      // Paywall nudge signals — see checkAccess for the trigger logic.
      upgradePromptDue: paywallState.upgradePromptDue,
      trialDaysLeft:    paywallState.trialDaysLeft,
      paywallEventCount: paywallState.eventCount,
      subscribed:       paywallState.subscribed,
    }}>
      {children}
    </AuthContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuthContext() {
  return useContext(AuthContext)
}
